
import Person         from './Person'
import Place          from './Place'
import PostalAddress  from './PostalAddress'
import Thing          from './Thing'
import Word           from './Word'

/**
 * An event happening at a certain time and location, such as a concert, lecture, or festival.
 * @memberOf things
 */
class Event extends Thing
{
    /**
     * Creates a new Event instance.
     * @constructor
     * @param object The generic object to initialize the object.
     */
    constructor( object = null )
    {
        super() ;

        /**
         * An actor, e.g. in tv, radio, movie, video games etc., or in an event. Actors can be associated with individual items or with a series, episode, clip.
         * @type {Person|Array}
         */
        this.actor = null ;

        /**
         * The duration of the item (movie, audio recording, event, etc.) in ISO 8601 date format or with a number value.
         * @type {string|number}
         */
        this.duration = null ;

        /**
         * The end date and time of the item (in ISO 8601 date format - https://en.wikipedia.org/wiki/ISO_8601).
         * @type {string}
         */
        this.endDate = null ;

        /**
         * An eventStatus of an event represents its status; particularly useful when an event is cancelled or rescheduled.
         * @type {string|Work}
         */
        this.eventStatus = null ;

        /**
         * The location of for example where the event is happening, an organization is located, or where an action takes place.
         * @type {PostalAddress|Place|string}
         */
        this.location = null ;

        /**
         * Used in conjunction with eventStatus for rescheduled or cancelled events. This property contains the previously scheduled start date. For rescheduled events, the startDate property should be used for the newly scheduled start date. In the (rare) case of an event that has been postponed and rescheduled multiple times, this field may be repeated.
         * @type {Array|string}
         */
        this.previousStartDate = null ;

        /**
         * The start date and time of the item (in ISO 8601 date format).
         * @type {string}
         */
        this.startDate = null ;

        /**
         * An Event that is part of this event. For example, a conference event includes many presentations, each of which is a subEvent of the conference.
         * @type {Event}
         */
        this.subEvent = null ;

        /**
         * An event that this event is a part of. For example, a collection of individual music performances might each have a music festival as their superEvent. Inverse property: subEvent.
         * @type {Event}
         */
        this.superEvent = null ;

        /**
         * The type of the Event.
         * @type {Word}
         */
        this.type = null ;

        this.set( object ) ;
    }

    /**
     * Clear the object.
     * @return {Object} The current item reference.
     */
    clear()
    {
        super.clear() ;
        this.actor = null ;
        this.duration = null ;
        this.endDate = null ;
        this.eventStatus = null ;
        this.location = null ;
        this.previousStartDate = null ;
        this.startDate = null ;
        this.subEvent = null ;
        this.superEvent = null ;
        this.type = null ;
        return this ;
    }

    /**
     * Returns a shallow copy of the object.
     * @return {Thing} a shallow copy of the object.
     */
    clone()
    {
        return new Event( this.toObject() ) ;
    }

    /**
     * Populate the object with specific behaviors or types. Launched by default in the constructor and in the set methods.
     * @returns {Thing} The current reference.
     */
    populate()
    {
        let { actor , eventStatus , location , subEvent , superEvent , type } = this ;
        if( actor )
        {
            if( actor instanceof Array )
            {
                this.actor = actor.map( item => item instanceof Person ? item : new Person(item) ) ;
            }
            else
            {
                this.actor = new Person( actor ) ;
            }
        }

        if( eventStatus )
        {
            this.eventStatus = eventStatus instanceof Word ? eventStatus : new Word(eventStatus) ;
        }

        if( location )
        {
            if( Place.is( this.location ) )
            {
                this.location = location instanceof Place ? location : new Place(location) ;
            }
            else if( PostalAddress.is( this.location ))
            {
                this.location = location instanceof PostalAddress ? location : new PostalAddress(location) ;
            }
        }

        if( subEvent )
        {
            if( subEvent instanceof Array )
            {
                this.subEvent = subEvent.map( item => item instanceof Event ? item : new Event(item) ) ;
            }
            else
            {
                this.subEvent = new Event( subEvent ) ;
            }
        }

        if( superEvent )
        {
            this.superEvent = superEvent instanceof Event ? superEvent : new Word(superEvent) ;
        }

        if( type )
        {
            this.type = type instanceof Word ? type : new Word(type) ;
        }

        return this ;
    }

    /**
     * Returns the generic object representation of the item.
     * @return {Object} The generic object representation of the item.
     */
    toObject()
    {
        let {
            actor , duration , endDate , eventStatus , location ,
            previousStartDate , startDate , subEvent , superEvent , type
        } = this ;

        if( actor instanceof Array )
        {
            actor = actor.map( item => item instanceof Person ? item.toObject() : item ) ;
        }
        else if ( actor instanceof Person )
        {
            actor = actor.toObject() ;
        }

        if( location instanceof PostalAddress || location instanceof Place )
        {
            location = location.toObject() ;
        }

        if( previousStartDate instanceof Array )
        {
            previousStartDate = [ ...previousStartDate ] ; // clone
        }

        if( subEvent instanceof Array )
        {
            subEvent = subEvent.map( item => item instanceof Event ? item.toObject() : item ) ;
        }
        else if ( subEvent instanceof Event )
        {
            subEvent = subEvent.toObject() ;
        }

        return { ...super.toObject() , ...{
            actor             : actor ,
            duration          : duration ,
            endDate           : endDate ,
            eventStatus       : eventStatus instanceof Word ? eventStatus.toObject() : eventStatus ,
            location          : location ,
            previousStartDate : previousStartDate ,
            startDate         : startDate ,
            subEvent          : subEvent ,
            superEvent        : superEvent instanceof Event ? superEvent.toObject() : superEvent ,
            type              : type instanceof Word ? type.toObject() : type
        }}
    }
}

export default Event ;


// WEBPACK FOOTER //
// ./src/app/things/Event.js